import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { FaClock } from "react-icons/fa";
import api, { BASE_URL } from "../../Utils/api";
import Modal from "react-bootstrap/Modal";
import { GlobalContext } from "../../Utils/GlobalContext";
import ClassificacaoTiposDocumentoModal from "../ClassificacaoTiposDocumento/ClassificacaoTiposDocumentoModal";
import TiposPagamentoModal from "../TiposPagamento/TiposPagamentoModal";
import { can } from "../../Utils/controlaPermissao";
import "./TiposDocumentoModal.css";


const TiposDocumentoModal = ({
  show,
  handleClose,
  documento,
  idEntidade,
  fetchTiposDocumento,
  onVincularAgendamento,
}) => {
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [id_classificacao, setIdClassificacao] = useState("");
  const [situacao, setSituacao] = useState("A");
  const [id_tipo_pagamento_padrao, setIdTipoPagamentoPadrao] = useState("");
  const [classificacoes, setClassificacoes] = useState([]);
  const [tiposPagamento, setTiposPagamento] = useState([]);
  const [loadingClassificacoes, setLoadingClassificacoes] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showClassificacaoModal, setShowClassificacaoModal] = useState(false);
  const [showTipoPagamentoModal, setShowTipoPagamentoModal] = useState(false);
  const idsClassificacoesAntesCadastroRef = useRef(new Set());
  const idsTiposPagamentoAntesCadastroRef = useRef(new Set());
  const { globalData } = useContext(GlobalContext);
  const entidadeID = idEntidade ?? globalData?.entidade_id ?? localStorage.getItem("entidade_id");
  const permiteCadastrarClassificacao = can(globalData, "tipos_documento");
  const permiteCadastrarTipoPagamento = can(globalData, "tipos_pagamento");
  const mensagemSemPermissao = "usuário sem permissão para acessar este recurso";

  useEffect(() => {
    if (!show) return;
    setSaveError("");
    if (documento) {
      setNome(documento.nome || "");
      setDescricao(documento.descricao || "");
      setIdClassificacao(documento.id_classificacao != null ? String(documento.id_classificacao) : "");
      setSituacao(documento.situacao || "A");
      setIdTipoPagamentoPadrao(documento.id_tipo_pagamento_padrao != null ? String(documento.id_tipo_pagamento_padrao) : "");
    } else {
      setNome("");
      setDescricao("");
      setIdClassificacao("");
      setSituacao("A");
      setIdTipoPagamentoPadrao("");
    }
  }, [show, documento]);

  const recarregarClassificacoes = useCallback(async (idClassificacaoPreferencial = null, selecionarCriado = false) => {
    if (!entidadeID) {
      setClassificacoes([]);
      return [];
    }
    const token = localStorage.getItem("authToken");
    const headers = { Authorization: token ? `Bearer ${token}` : "" };
    setLoadingClassificacoes(true);
    try {
      const res = await api.get(
        `${BASE_URL}/api/classificacaoTiposDocumento/ListarClassificacaoTiposDocumentoAtivas/${entidadeID}`,
        { headers }
      );
      const lista = Array.isArray(res.data?.data) ? res.data.data : [];
      setClassificacoes(lista);
      const classificacaoNova = selecionarCriado
        ? lista.find((item) => !idsClassificacoesAntesCadastroRef.current.has(String(item.id)))
        : null;
      const idParaSelecionar = idClassificacaoPreferencial ?? classificacaoNova?.id;
      if (idParaSelecionar != null) {
        setIdClassificacao(String(idParaSelecionar));
      }
      idsClassificacoesAntesCadastroRef.current = new Set(
        lista.map((item) => String(item.id))
      );
      return lista;
    } catch {
      setClassificacoes([]);
      return [];
    } finally {
      setLoadingClassificacoes(false);
    }
  }, [entidadeID]);

  const recarregarTiposPagamento = useCallback(async (idTipoPagamentoPreferencial = null, selecionarCriado = false) => {
    if (!entidadeID) {
      setTiposPagamento([]);
      return [];
    }
    const token = localStorage.getItem("authToken");
    const headers = { Authorization: token ? `Bearer ${token}` : "" };
    try {
      const res = await api.get(
        `${BASE_URL}/api/tiposPagamento/ListarTiposPagamento/${entidadeID}`,
        { headers }
      );
      const lista = Array.isArray(res.data?.data) ? res.data.data : [];
      setTiposPagamento(lista);
      const tipoNovo = selecionarCriado
        ? lista.find((item) => !idsTiposPagamentoAntesCadastroRef.current.has(String(item.id)))
        : null;
      const idParaSelecionar = idTipoPagamentoPreferencial ?? tipoNovo?.id;
      if (idParaSelecionar != null) {
        setIdTipoPagamentoPadrao(String(idParaSelecionar));
      }
      idsTiposPagamentoAntesCadastroRef.current = new Set(
        lista.map((item) => String(item.id))
      );
      return lista;
    } catch {
      setTiposPagamento([]);
      return [];
    }
  }, [entidadeID]);

  useEffect(() => {
    if (!show || !entidadeID) return;
    recarregarClassificacoes();
    recarregarTiposPagamento();
  }, [show, entidadeID, recarregarClassificacoes, recarregarTiposPagamento]);

  const abrirCadastroClassificacao = useCallback(() => {
    if (!permiteCadastrarClassificacao) return;
    idsClassificacoesAntesCadastroRef.current = new Set(
      classificacoes.map((item) => String(item.id))
    );
    setShowClassificacaoModal(true);
  }, [classificacoes, permiteCadastrarClassificacao]);

  const abrirCadastroTipoPagamento = useCallback(() => {
    if (!permiteCadastrarTipoPagamento) return;
    idsTiposPagamentoAntesCadastroRef.current = new Set(
      tiposPagamento.map((item) => String(item.id))
    );
    setShowTipoPagamentoModal(true);
  }, [permiteCadastrarTipoPagamento, tiposPagamento]);

  const handleCloseClassificacaoModal = useCallback(async (resultado) => {
    setShowClassificacaoModal(false);
    const idCriado = resultado?.id ?? resultado?.id_classificacao ?? resultado?.id_classificacao_tipo_documento;
    await recarregarClassificacoes(idCriado != null ? String(idCriado) : null, true);
  }, [recarregarClassificacoes]);

  const handleCloseTipoPagamentoModal = useCallback(async (resultado) => {
    setShowTipoPagamentoModal(false);
    const idCriado = resultado?.id ?? resultado?.id_tipo_pagamento;
    await recarregarTiposPagamento(idCriado != null ? String(idCriado) : null, true);
  }, [recarregarTiposPagamento]);

  const handleSave = async () => {
    setSaveError("");
    if (!nome || !nome.trim()) {
      setSaveError("Nome é obrigatório.");
      return;
    }
    if (!documento && (id_classificacao === "" || id_classificacao == null)) {
      setSaveError("Classificação é obrigatória.");
      return;
    }
    const documentoData = {
      nome: nome.trim(),
      descricao: descricao || null,
      situacao,
      id_tipo_pagamento_padrao: id_tipo_pagamento_padrao !== "" && id_tipo_pagamento_padrao != null ? Number(id_tipo_pagamento_padrao) : null,
    };
    if (id_classificacao !== "" && id_classificacao != null) {
      documentoData.id_classificacao = Number(id_classificacao) || null;
    }
    const token = localStorage.getItem("authToken");
    const headers = { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" };
    try {
      if (documento) {
        await api.put(
          `${BASE_URL}/api/tiposDocumento/EditarTiposDocumento/${documento.id}`,
          documentoData,
          { headers }
        );
        fetchTiposDocumento();
        handleClose();
      } else {
        const res = await api.post(
          `${BASE_URL}/api/tiposDocumento/InserirTiposDocumento/${entidadeID}`,
          documentoData,
          { headers }
        );
        fetchTiposDocumento();
        // Backend retorna { success, data: { tipoDocumentoId, id_tipo_documento, ... } } — passar id para abrir modal Configurações de Campos com esse id na rota
        const data = res.data.data;
        const idTipoDocumento = data?.id_tipo_documento ?? data?.tipoDocumentoId ?? data?.id;
        const idTipoConfiguracaoCampos =
          data?.id_tipo_configuracoes_campos ??
          data?.tipoConfiguracaoId ??
          data?.id_tipo_configuracao ??
          null;
        if (idTipoDocumento != null) {
          handleClose({
            id: idTipoDocumento,
            id_tipo_documento: idTipoDocumento,
            id_tipo_configuracoes_campos: idTipoConfiguracaoCampos,
            nome: nome.trim(),
          });
        } else {
          handleClose();
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Erro ao salvar tipo de documento.";
      setSaveError(msg);
      console.error("Erro ao salvar tipo de documento:", err);
    }
  };

  return (
    <>
    <Modal show={show} onHide={handleClose} backdrop="static" dialogClassName="modal-dialog-top">
      <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
          {documento ? "Tipo de Documento" : "Adicionar Tipo de Documento"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
        {saveError && (
          <div className="alert alert-danger" role="alert">
            {saveError}
          </div>
        )}
        <Form>
          <Form.Group controlId="formNome" className="mb-3">
            <Form.Label>Nome *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Informe o nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="formClassificacao" className="mb-3">
            <Form.Label>Classificação *</Form.Label>
            <div className="td-select-add-combo">
            <Form.Select
              className="td-select-add-combo-select"
              value={id_classificacao}
              onChange={(e) => setIdClassificacao(e.target.value)}
              disabled={loadingClassificacoes}
            >
              <option value="">{loadingClassificacoes ? "Carregando..." : "Selecione a classificação"}</option>
              {classificacoes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome || `ID ${c.id}`}
                </option>
              ))}
            </Form.Select>
            <span
              className="td-select-add-combo-button-wrapper"
              title={permiteCadastrarClassificacao ? "Adicionar classificacao" : mensagemSemPermissao}
            >
              <button
                type="button"
                className="td-select-add-combo-button"
                onClick={abrirCadastroClassificacao}
                disabled={!permiteCadastrarClassificacao}
                aria-label="Adicionar classificacao"
                title={permiteCadastrarClassificacao ? "Adicionar classificacao" : undefined}
              >
                <span className="td-select-add-action-circle" aria-hidden="true" />
              </button>
            </span>
            </div>
          </Form.Group>
          <Form.Group controlId="formDescricao" className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Informe a descrição"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
            />
          </Form.Group>
          <Form.Group controlId="formTipoPagamentoPadrao" className="mb-3">
            <Form.Label>Tipo de Pagamento Padrão</Form.Label>
            <div className="td-select-add-combo">
            <Form.Select
              className="td-select-add-combo-select"
              value={id_tipo_pagamento_padrao}
              onChange={(e) => setIdTipoPagamentoPadrao(e.target.value)}
            >
              <option value="">Nenhum</option>
              {tiposPagamento.map((tp) => (
                <option key={tp.id} value={tp.id}>
                  {tp.descricao || `ID ${tp.id}`}
                </option>
              ))}
            </Form.Select>
            <span
              className="td-select-add-combo-button-wrapper"
              title={permiteCadastrarTipoPagamento ? "Adicionar tipo de pagamento" : mensagemSemPermissao}
            >
              <button
                type="button"
                className="td-select-add-combo-button"
                onClick={abrirCadastroTipoPagamento}
                disabled={!permiteCadastrarTipoPagamento}
                aria-label="Adicionar tipo de pagamento padrao"
                title={permiteCadastrarTipoPagamento ? "Adicionar tipo de pagamento" : undefined}
              >
                <span className="td-select-add-action-circle" aria-hidden="true" />
              </button>
            </span>
            </div>
          </Form.Group>
          <Form.Group controlId="formSituacao" className="mb-3">
            <Form.Label>Situação</Form.Label>
            <Form.Select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
              <option value="A">Ativo</option>
              <option value="I">Inativo</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda">
          {documento && onVincularAgendamento && (
            <Button
              variant="outline-secondary"
              onClick={() => onVincularAgendamento(documento)}
              title="Vincular Tipos de Agendamento"
            >
              <FaClock />
            </Button>
          )}
        </div>
        <div className="modal-footer-acoes-direita">
          <Button variant="light" className="modal-footer-btn-cancelar" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="success" className="modal-footer-btn-salvar" onClick={handleSave}>
            Salvar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
    <ClassificacaoTiposDocumentoModal
      show={showClassificacaoModal}
      handleClose={handleCloseClassificacaoModal}
      item={null}
      idEntidade={entidadeID}
      fetchLista={() => {}}
    />
    <TiposPagamentoModal
      show={showTipoPagamentoModal}
      handleClose={handleCloseTipoPagamentoModal}
      tipoPagamento={null}
      fetchTiposPagamento={() => {}}
    />
    </>
  );
};

export default TiposDocumentoModal;
